<template>
  <div v-if="isShowSkeleton || modeList.length" class="nav-subsections">
    <SkeletonNavSubsect v-if="isShowSkeleton" :error="get_sectionList.error" />

    <div
      v-if="modeList.length"
      ref="slider"
      v-swiper:slider="$options.static.swiperOption"
      class="nav-subsections__wrapper swiper"
    >
      <div class="nav-subsections__list swiper-wrapper">
        <div
          v-for="(item) in modeList"
          :key="item.term"
          class="nav-subsections__item swiper-slide"
        >
          <UiBtn
            class="nav-subsections__btn"
            color="gray"
            :text="item.name"
            :icon="item.icon"
            :icon-src="item.iconSrc"
            size-small-mobile
            :to="item.url"
            :active="item.active"
          />
        </div>
      </div>
    </div>

    <template v-if="$slots.actions">
      <div class="nav-subsections__divider" />

      <div class="nav-subsections__actions">
        <slot name="actions" />
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { PARAM_SUB_PAGE, VIEW_MODE, QUERY, LIST_TYPE } from '@/components/Lobby/enum.js'
import SkeletonNavSubsect from '~/components/Lobby/Skeletons/SkeletonNavSubsect'
import { scrollToUnvisibleSlide } from '~/util/helpers/sliderHelper'
import { getLangText } from '@/components/Lobby/helpers.js'

const swiperOption = {
  slidesPerView: 'auto',
  freeMode: true,
  centerInsufficientSlides: true,
  watchOverflow: true,
  shortSwipes: true,
  preventClicks: true,
  preventClicksPropagation: false,
  threshold: 12,
}

export default {
  name: 'NavSubsections',
  components: {
    SkeletonNavSubsect,
  },
  static: {
    swiperOption,
  },
  computed: {
    ...mapGetters('lobby', ['get_sectionList', 'get_currentSection']),
    currentSection() {
      return this.get_currentSection(this.$route)
    },
    subsectionList() {
      const subsections = this.currentSection?.subsections
      if (!subsections) return []

      return subsections.map((el) => {
        return {
          ...el,
          url: this.localePath(`/${this.currentSection?.term}/${el.term}`),
          iconSrc: `${this.$config.cloudUrl}${el.iconUrl}`,
          name: getLangText(el.nameUa, el.nameRu, this.$i18n),
          active: this.$route.params[PARAM_SUB_PAGE] === el.term,
        }
      })
    },
    isDefaultMode() {
      return this.currentSection?.viewMode === VIEW_MODE.DEFAULT
    },
    viewModeList() {
      const isProvider = this.currentSection?.listType === LIST_TYPE.PROVIDER.NAME
      const isCollection = this.currentSection?.listType === LIST_TYPE.COLLECTION.NAME

      let name1 = this.$tc('lobby.navs.textDefault1')
      let name2 = this.$tc('lobby.navs.textDefault2')
      let icon1 = 'lobby/grids-flat'
      let icon2 = 'lobby/grids-ladder'

      if (isProvider) {
        name1 = this.$tc('lobby.navs.textProvider1')
        name2 = this.$tc('lobby.navs.textProvider2')
        icon1 = 'lobby/grids-flat'
        icon2 = 'lobby/audio-games'
      }
      if (isCollection) {
        name1 = this.$tc('lobby.navs.textCollection1')
        name2 = this.$tc('lobby.navs.textCollection2')
        icon1 = 'lobby/grids-flat'
        icon2 = 'lobby/grids-ladder'
      }

      return [
        {
          name: name1,
          icon: icon1,
          url: { query: {} },
          active: !this.$route.query[QUERY.LAYOUT],
        },
        {
          name: name2,
          icon: icon2,
          url: { query: { [QUERY.LAYOUT]: VIEW_MODE.EXPAND } },
          active: this.$route.query[QUERY.LAYOUT] === VIEW_MODE.EXPAND,
        },
      ]
    },
    modeList() {
      if (!this.currentSection) return []

      const isDefaultMode = this.currentSection?.viewMode === VIEW_MODE.DEFAULT

      return isDefaultMode ? this.subsectionList : this.viewModeList
    },
    isShowSkeleton() {
      return (this.get_sectionList.isLoading || this.get_sectionList.error) && !this.get_sectionList.data
    },
  },
  watch: {
    '$route.path'() {
      this.$nextTick(() => {
        this.slider.update()
      })
    },
  },
  mounted() {
    this.scrollIntoView()

    this.slider?.on('update', this.scrollIntoView)
  },
  methods: {
    scrollIntoView() {
      const activeIndex = this.modeList.findIndex((el) => {
        return this.$route.params[PARAM_SUB_PAGE] === el.term
      })

      scrollToUnvisibleSlide(activeIndex, this.slider)
    },
  },
}
</script>

<style lang="scss" src="./style.scss" scoped></style>
